import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  COLOR_ERROR_1,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_300,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_200,
  COLOR_PRIMARY_2_300,
  COLOR_PRIMARY_2_400,
  COLOR_PRIMARY_2_500,
  COLOR_PRIMARY_2_600,
  COLOR_PRIMARY_2_700,
  COLOR_PRIMARY_2_800,
  COLOR_SECONDARY_2,
  COLOR_SUCCESS_1,
  COLOR_TERTIARY_1,
  DEFAULT_TEXT_COLOR,
  KPI_CARD_COLOR,
} from 'src/colors';
import {
  Box,
  ClickAwayListener,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import {
  COLOR_E,
  EXPERT_CTA,
  EXPERT_CTA_FOR_TAILOR_REGION,
  PRO_CTA,
  UNLOCK_CTA,
  exchangeRates,
} from 'src/const';
import {
  abbreviateNumber,
  checkUserTailorMarketAccess,
  convertCurrency,
  formatCurrency,
  getDummyMarketURL,
  getSeasonality,
  getSeasonalityBarColor,
  isEmpty,
  roundNumber,
  trackLast3YearsDataInterest,
} from 'src/utils/functions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { overviewPageCustomTooltipProps } from 'src/interfaces/idashboard';
import InfoTooltip from 'src/App/HOC/Tooltips/InfoTooltip';
import TooltipCard from 'src/App/HOC/Cards/TooltipCard';
import { getHistoricalData } from '../helper';
import { User } from 'src/interfaces/iuser';

const MainKPICard = ({
  isDisabled,
  constantDummyData,
  currency,
  marketLevelOccupancyRate,
  occupancyYoYChange,
  marketLevelRevenuePotential,
  marketLevelDailyRate,
  revenueYoYChange,
  dailyYoYChange,
  marketLevelActiveListings,
  marketLevelCleaningFee,
  marketLevelBookingCount,
  listingYoYChange,
  occupancyRateHistoricalData,
  occupancyPast6MonthsHistoricalData,
  revenueHistoricalData,
  revenuePast6MonthsHistoricalData,
  dailyRateHistoricalData,
  dailyRatePast6MonthsHistoricalData,
  listingHistoricalData,
  listingPast6MonthsHistoricalData,
  matches,
  handleOnSampleClick,
  dummyMarket,
  search,
  selectedPercentile,
  setSelectedpercentile,
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const dummyDataFromSampleMarket = useSelector(state => (state as any).dashboard.lockedMarketData);
  const [dummyData, setDummyData] = useState<any>(constantDummyData);
  const [selectedMainKpi, setSelectedMainKpi] = useState<string>('occupancy');
  const [timePeriod, setTimePeriod] = useState<number>(36);
  const [isOccupancyTooltipOpen, setIsOccupancyTooltipOpen] = useState(false);
  const [isRevenueTooltipOpen, setIsRevenueTooltipOpen] = useState(false);
  const [isNightlyRateTooltipOpen, setIsNightlyRateTooltipOpen] = useState(false);
  const [isListingTooltipOpen, setIsListingTooltipOpen] = useState(false);
  const [seasonalityData, setSeasonalityData] = useState({});
  const currentPath = window.location.pathname;
  const accomodationRevenueInTooltip = roundNumber(marketLevelDailyRate * (marketLevelOccupancyRate/100) * 365)
  const cleaningFeeRevenueInTooltip = marketLevelBookingCount * marketLevelCleaningFee
  useEffect(() => {
    if (dummyDataFromSampleMarket.data) {
      setDummyData(dummyDataFromSampleMarket.data.historical_monthly_performance);
    }
  }, [dummyDataFromSampleMarket]);
  // console.log(">>marketLevelRevenuePotential",marketLevelRevenuePotential)

  let dummyMarketURL = getDummyMarketURL(dummyMarket);

  const isDemoMarket = currentPath.toLowerCase().includes(dummyMarketURL);

  let userTailorMarketAccess = checkUserTailorMarketAccess(user, search);

  const isAccessDenied =
    (isDisabled ||
      (user.subscriptionPlan != 'expert' && timePeriod == 36) ||
      !userTailorMarketAccess) &&
    !isDemoMarket;

  const GRAPH_DATA_MAPPING = {
    occupancy: {
      36: occupancyRateHistoricalData,
      24: occupancyRateHistoricalData,
      12: occupancyRateHistoricalData,
      6: occupancyPast6MonthsHistoricalData,
    },
    revenue: {
      36: revenueHistoricalData,
      24: revenueHistoricalData,
      12: revenueHistoricalData,
      6: revenuePast6MonthsHistoricalData,
    },
    daily: {
      36: dailyRateHistoricalData,
      24: dailyRateHistoricalData,
      12: dailyRateHistoricalData,
      6: dailyRatePast6MonthsHistoricalData,
    },
    listing: {
      36: listingHistoricalData,
      24: listingHistoricalData,
      12: listingHistoricalData,
      6: listingPast6MonthsHistoricalData,
    },
  };

  const mainKpiOccupancyDummyData = getHistoricalData(dummyData, 'occupancy', currency)[0];
  const mainKpiRevenueDummyData = getHistoricalData(dummyData, 'revenue', currency)[0];
  const mainKpiListingDummydata = getHistoricalData(dummyData, 'listing', currency)[0];
  const mainKpiDailyRateDummydata = getHistoricalData(dummyData, 'daily', currency)[0];

  const GRAPH_DUMMY_DATA_MAPPING = {
    occupancy: {
      36: mainKpiOccupancyDummyData,
      24: mainKpiOccupancyDummyData,
      12: mainKpiOccupancyDummyData,
      6: mainKpiOccupancyDummyData.slice(-6),
    },
    revenue: {
      36: mainKpiRevenueDummyData,
      24: mainKpiRevenueDummyData,
      12: mainKpiRevenueDummyData,
      6: mainKpiRevenueDummyData.slice(-6),
    },
    daily: {
      36: mainKpiDailyRateDummydata,
      24: mainKpiDailyRateDummydata,
      12: mainKpiDailyRateDummydata,
      6: mainKpiDailyRateDummydata.slice(-6),
    },
    listing: {
      36: mainKpiListingDummydata,
      24: mainKpiListingDummydata,
      12: mainKpiListingDummydata,
      6: mainKpiListingDummydata.slice(-6),
    },
  };

  const GRAPH_LINE_DATA_MAPPING = {
    occupancy: {
      25: 'occupancy_rate25',
      50: 'occupancy_rate50',
      75: 'occupancy_rate75',
      90: 'occupancy_rate90',
    },
    revenue: { 25: 'revenue25', 50: 'revenue50', 75: 'revenue75', 90: 'revenue90' },
    daily: { 25: 'daily_rate25', 50: 'daily_rate50', 75: 'daily_rate75', 90: 'daily_rate90' },
    listing: {
      25: 'active_listings_count',
      50: 'active_listings_count',
      75: 'active_listings_count',
      90: 'active_listings_count',
    },
  };

  useEffect(() => {
    let dataUsedToCalculateSeasonality = isAccessDenied
      ? GRAPH_DUMMY_DATA_MAPPING['revenue'][timePeriod]
      : GRAPH_DATA_MAPPING['revenue'][timePeriod];
    let seasonilityData = getSeasonality(
      dataUsedToCalculateSeasonality,
      'overview',
      selectedPercentile
    );
    setSeasonalityData(seasonilityData);
  }, [
    timePeriod,
    revenueHistoricalData,
    revenuePast6MonthsHistoricalData,
    selectedPercentile,
    isAccessDenied,
  ]);

  useEffect(() => {
    console.log('[DEV-LOG] Main KPI has rendered');
  }, []);

  useEffect(() => {
    if (user.subscriptionPlan === 'pro') {
      setTimePeriod(24);
    }
  }, [user.subscriptionPlan]);

  const handleTimePeriodChange = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    setTimePeriod(value);
    if (value == 36) trackLast3YearsDataInterest();
  };

  const combineFormatterYAxis = (val: number) => {
    if (selectedMainKpi.includes('revenue') || selectedMainKpi.includes('daily')) {
      if (val > 1000) {
        return `${currency && exchangeRates[currency].symbol}` + abbreviateNumber(val);
      } else {
        return `${currency && exchangeRates[currency].symbol}` + abbreviateNumber(val, false);
      }
    } else if (selectedMainKpi.includes('occupancy')) {
      return val + '%';
    } else {
      return abbreviateNumber(val, false);
    }
  };

  const CustomLegend = props => {
    const { payload } = props;
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{ borderTop: '4px solid #037A90', width: '15px', position: 'relative', top: '5px' }}
          />
          <Typography sx={{ fontSize: '10px' }}>Last 12 Months </Typography>
        </Box>

        {timePeriod == 24 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                borderTop: '2px dashed #4FA2B1',
                width: '15px',
                position: 'relative',
                top: '5px',
              }}
            />
            <Typography sx={{ fontSize: '10px' }}>Preceding Period</Typography>
          </Box>
        )}

        {timePeriod == 36 && (
          <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
            <Box
              sx={{
                borderTop: `4px solid ${COLOR_PRIMARY_1_800}`,
                width: '15px',
                position: 'relative',
                top: '5px',
              }}
            />
            <Typography sx={{ fontSize: '10px' }}>Previous Year</Typography>
          </Box>
        )}

        {timePeriod == 36 && (
          <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
            <Box
              sx={{
                borderTop: `4px solid ${COLOR_PRIMARY_2_300}`,
                width: '15px',
                position: 'relative',
                top: '5px',
              }}
            />
            <Typography sx={{ fontSize: '10px' }}>2 Years Ago</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const CustomTooltip: React.FC<overviewPageCustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      let kpiName = '';

      if (selectedMainKpi == 'revenue') {
        kpiName = 'Revenue';
      } else if (selectedMainKpi == 'occupancy') {
        kpiName = 'Occupancy Rate';
      } else if (selectedMainKpi == 'daily') {
        kpiName = 'Nightly Rate';
      } else {
        kpiName = 'Active Listing Count';
      }

      return (
        <Box
          sx={{
            height: '120px',
            width: '150px',
            backgroundColor: '#ffff',
            borderRadius: '10px',
            boxShadow: '0 4px 10px #0003',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
          }}
        >
          <Typography variant="body1" style={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
            {kpiName}
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
            {payload[0]['payload'].month}
          </Typography>
          {payload[0] && (
            <Typography variant="body1">
              <span>{payload[0]['payload']['latestYear']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_2 }}>
                {kpiName != 'Occupancy Rate' && kpiName != 'Active Listing Count'
                  ? `${currency && exchangeRates[currency].symbol}`
                  : ''}
                {formatCurrency(payload[0].value.toString())}
                {kpiName == 'Occupancy Rate' ? '%' : ''}
              </span>
            </Typography>
          )}
          {payload[1] && (
            <Typography variant="body1">
              <span>{payload[1]['payload']['previousYear']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_1_800 }}>
                {kpiName != 'Occupancy Rate' && kpiName != 'Active Listing Count'
                  ? `${currency && exchangeRates[currency].symbol}`
                  : ''}
                {formatCurrency(payload[1].value.toString())}
                {kpiName == 'Occupancy Rate' ? '%' : ''}
              </span>
            </Typography>
          )}
          {payload[2] && (
            <Typography variant="body1">
              <span>{payload[2]['payload']['twoYearsBefore']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_2_300 }}>
                {kpiName != 'Occupancy Rate' && kpiName != 'Active Listing Count'
                  ? `${currency && exchangeRates[currency].symbol}`
                  : ''}
                {formatCurrency(payload[2].value.toString())}
                {kpiName == 'Occupancy Rate' ? '%' : ''}
              </span>
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const isGraphDataPresent: boolean = isAccessDenied
    ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod].length > 0
    : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod].length > 0;

  const upgradeMessage = () => {
    if (!userTailorMarketAccess) {
      return EXPERT_CTA_FOR_TAILOR_REGION;
    } else if (user.subscriptionPlan === 'starter') {
      return PRO_CTA;
    } else if (timePeriod === 36 && user.subscriptionPlan !== 'expert') {
      return EXPERT_CTA;
    } else {
      return UNLOCK_CTA;
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box
        sx={{
          height: '25%',
          width: '97%',
          display: 'flex',
          padding: matches ? '10px' : '10px 20px',
          gap: matches ? 1 : 2,
        }}
      >
        {/* occupancy rate box */}
        <Box
          sx={{
            height: '100%',
            flex: 1,
            ...(selectedMainKpi === 'occupancy' && {
              boxShadow: '0 4px 10px #0001',
            }),
            ':hover': {
              cursor: 'pointer',
            },
          }}
          onClick={() => {
            setSelectedMainKpi('occupancy');
          }}
        >
          {selectedMainKpi === 'occupancy' && (
            <Box
              sx={{
                height: '4%',
                width: '100%',
                backgroundColor: KPI_CARD_COLOR,
              }}
            ></Box>
          )}
          <Box
            sx={{
              height: '28%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              color: '#000',
              padding: matches ? '3px' : '3px 10px',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: '500', fontSize: matches ? '12px' : '' }}>
              {'Occupancy Rate'}
            </Typography>
            <ClickAwayListener onClickAway={() => setIsOccupancyTooltipOpen(false)}>
              <InfoTooltip
                title={
                  <TooltipCard
                    title={'Occupancy (Median)'}
                    content={
                      'How often was a typical active property occupied over the last 12 months?'
                    }
                    link=""
                  />
                }
                enterTouchDelay={0}
                leaveTouchDelay={6000}
                open={isOccupancyTooltipOpen}
                onOpen={() => setIsOccupancyTooltipOpen(true)}
                onClose={() => setIsOccupancyTooltipOpen(false)}
              >
                <IconButton
                  style={{ padding: 3 }}
                  onClick={() => setIsOccupancyTooltipOpen(prev => !prev)}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: matches ? '15px' : '17px', color: '#4aa59a' }}
                  />
                </IconButton>
              </InfoTooltip>
            </ClickAwayListener>
          </Box>
          <Box
            sx={{
              height: '40%',
              width: '100%',
              color: '#000',
              padding: '0px 10px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: '1000',
                fontSize: matches ? '20px' : '30px',
                color: selectedMainKpi === 'occupancy' ? KPI_CARD_COLOR : DEFAULT_TEXT_COLOR,
              }}
            >
              {' '}
              {marketLevelOccupancyRate}%
            </Typography>
          </Box>
          {selectedMainKpi === 'occupancy' && (
            <Box
              sx={{
                height: '28%',
                width: '100%',
                color: '#000',
                textAlign: 'end',
                position: 'relative',
                top: '-5px',
                right: '5px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  //   padding: '0px 20px 5px 0px',
                  color:
                    occupancyYoYChange && Number(occupancyYoYChange) > 0
                      ? COLOR_SUCCESS_1
                      : COLOR_ERROR_1,
                }}
              >
                {matches ? '' : 'YoY Change:'}{' '}
                {occupancyYoYChange ? (
                  occupancyYoYChange
                ) : (
                  <LockSharpIcon style={{ fontSize: 20, color: 'inherit' }} />
                )}
                {occupancyYoYChange != 'N/A' ? '%' : ''}{' '}
              </Typography>
            </Box>
          )}
        </Box>

        {/* revenue Box */}
        <Box
          sx={{
            height: '100%',
            flex: 1,
            ...(selectedMainKpi === 'revenue' && {
              boxShadow: '0 4px 10px #0001',
            }),
            ':hover': {
              cursor: 'pointer',
            },
          }}
          onClick={() => {
            setSelectedMainKpi('revenue');
          }}
        >
          {selectedMainKpi === 'revenue' && (
            <Box
              sx={{
                height: '4%',
                width: '100%',
                backgroundColor: KPI_CARD_COLOR,
              }}
            ></Box>
          )}
          <Grid container>
            <Grid item xs={1}>
              {!isEmpty(seasonalityData) && seasonalityData['highestRevenue'] && (
                <Grid
                  item
                  container
                  sx={{
                    height: '100%',
                    paddingTop: '5px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <InfoTooltip
                    title={
                      <TooltipCard
                        title={`Seasonality: ${
                          isAccessDenied ? '' : `${seasonalityData['seasonality']}%`
                        }`}
                        content={
                          isAccessDenied
                            ? ''
                            : `Highest Revenue - ${
                                seasonalityData['highestRevenue']?.date.split(',')[0]
                              } (${exchangeRates[currency].symbol}${formatCurrency(
                                String(seasonalityData['highestRevenue']?.revenue)
                              )}) `
                        }
                        contentAdditional={
                          isAccessDenied
                            ? ''
                            : `Lowest Revenue - ${
                                seasonalityData['lowestRevenue']?.date.split(',')[0]
                              } (${exchangeRates[currency].symbol}${formatCurrency(
                                String(seasonalityData['lowestRevenue']?.revenue)
                              )}) `
                        }
                        isLocked={isAccessDenied}
                        handleOnSampleClick={handleOnSampleClick}
                      />
                    }
                  >
                    <div
                      style={{
                        width: 10,
                        borderRadius: 10,
                        height: `${seasonalityData['seasonality']}%`,
                        background: getSeasonalityBarColor(seasonalityData['seasonality']),
                      }}
                    ></div>
                  </InfoTooltip>
                </Grid>
              )}
            </Grid>
            <Grid item container xs={11}>
              <Grid
                item
                xs={12}
                sx={{
                  // height: '28%',
                  // width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  color: '#000',
                  padding: matches ? '3px' : '3px 10px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: '500', fontSize: matches ? '12px' : '' }}
                >
                  {'Revenue'}
                </Typography>
                <ClickAwayListener onClickAway={() => setIsRevenueTooltipOpen(false)}>
                  <InfoTooltip
                    title={
                      <TooltipCard
                        title={'Revenue'}
                        content={
                          `The total revenue of ${exchangeRates[currency].symbol + formatCurrency(roundNumber(convertCurrency(currency,marketLevelRevenuePotential, false,true)).toString())} is calculated as follows:`
                        }
                        contentAdditional2={""}
                        contentAdditional3={{
                          "heading": `Total Revenue (${exchangeRates[currency].symbol + formatCurrency(roundNumber(convertCurrency(currency,accomodationRevenueInTooltip + cleaningFeeRevenueInTooltip, false,true)).toString())}) = `,
                          "value": `${exchangeRates[currency].symbol + formatCurrency(roundNumber(convertCurrency(currency,accomodationRevenueInTooltip, false,true)).toString())} (Accommodation) + ${exchangeRates[currency].symbol + formatCurrency(roundNumber(convertCurrency(currency,cleaningFeeRevenueInTooltip, false,true)).toString())} (Cleaning Fee) `
                        }}
                        contentAdditional4={{
                          "heading": `Accommodation Revenue (${exchangeRates[currency].symbol + formatCurrency(roundNumber(convertCurrency(currency,accomodationRevenueInTooltip, false,true)).toString())}) = `,
                          "value": `${exchangeRates[currency].symbol +  formatCurrency(roundNumber(convertCurrency(currency,marketLevelDailyRate, false,true)).toString())} per night x 365 days x ${marketLevelOccupancyRate}% occupancy `
                        }}
                        contentAdditional5={{
                          "heading": `Cleaning Fees Revenue (${exchangeRates[currency].symbol +formatCurrency(roundNumber(convertCurrency(currency,cleaningFeeRevenueInTooltip, false,true)).toString())}) = `,
                          "value": `${marketLevelBookingCount} bookings x ${exchangeRates[currency].symbol + formatCurrency(roundNumber(convertCurrency(currency,marketLevelCleaningFee, false,true)).toString())} cleaning fee `
                        }}
                        link=""
                      />
                    }
                    enterTouchDelay={0}
                    leaveTouchDelay={6000}
                    open={isRevenueTooltipOpen}
                    onOpen={() => setIsRevenueTooltipOpen(true)}
                    onClose={() => setIsRevenueTooltipOpen(false)}
                  >
                    <IconButton
                      style={{ padding: 3 }}
                      onClick={() => setIsRevenueTooltipOpen(prev => !prev)}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: matches ? '15px' : '17px', color: '#4aa59a' }}
                      />
                    </IconButton>
                  </InfoTooltip>
                </ClickAwayListener>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  // height: '40%',
                  // width: '100%',
                  color: '#000',
                  padding: '0px 10px',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: '1000',
                    fontSize: matches ? '20px' : '30px',
                    color: selectedMainKpi === 'revenue' ? KPI_CARD_COLOR : DEFAULT_TEXT_COLOR,
                  }}
                >
                  {' '}
                  {currency && exchangeRates[currency].symbol}
                  {formatCurrency(
                    String(
                      matches
                        ? abbreviateNumber(convertCurrency(currency, marketLevelRevenuePotential))
                        : convertCurrency(currency, marketLevelRevenuePotential)
                    )
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {selectedMainKpi === 'revenue' && (
            <Box
              sx={{
                height: '28%',
                width: '100%',
                color: '#000',
                textAlign: 'end',
                position: 'relative',
                top: '-5px',
                right: '5px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  //   padding: '0px 20px 5px 0px',
                  color:
                    revenueYoYChange && Number(revenueYoYChange) > 0
                      ? COLOR_SUCCESS_1
                      : COLOR_ERROR_1,
                }}
              >
                {matches ? '' : 'YoY Change:'}{' '}
                {revenueYoYChange ? (
                  revenueYoYChange
                ) : (
                  <LockSharpIcon style={{ fontSize: 20, color: 'inherit' }} />
                )}
                {revenueYoYChange != 'N/A' ? '%' : ''}{' '}
              </Typography>
            </Box>
          )}
        </Box>

        {/* daily rate box */}
        <Box
          sx={{
            height: '100%',
            flex: 1,
            ...(selectedMainKpi === 'daily' && {
              boxShadow: '0 4px 10px #0001',
            }),
            ':hover': {
              cursor: 'pointer',
            },
          }}
          onClick={() => {
            setSelectedMainKpi('daily');
          }}
        >
          {selectedMainKpi === 'daily' && (
            <Box
              sx={{
                height: '4%',
                width: '100%',
                backgroundColor: KPI_CARD_COLOR,
              }}
            ></Box>
          )}
          <Box
            sx={{
              height: '28%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              color: '#000',
              gap: '3px',
              padding: matches ? '3px' : '3px 10px',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: '500', fontSize: matches ? '12px' : '' }}>
              Nightly rate
            </Typography>
            <ClickAwayListener onClickAway={() => setIsNightlyRateTooltipOpen(false)}>
              <InfoTooltip
                title={
                  <TooltipCard
                    title={'Nightly Rate (Median)'}
                    content={
                      'How much did hosts in this market charge per day over the last 12 months?'
                    }
                    link=""
                  />
                }
                enterTouchDelay={0}
                leaveTouchDelay={6000}
                open={isNightlyRateTooltipOpen}
                onOpen={() => setIsNightlyRateTooltipOpen(true)}
                onClose={() => setIsNightlyRateTooltipOpen(false)}
              >
                <IconButton
                  style={{ padding: 3 }}
                  onClick={() => setIsNightlyRateTooltipOpen(prev => !prev)}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: matches ? '15px' : '17px', color: '#4aa59a' }}
                  />
                </IconButton>
              </InfoTooltip>
            </ClickAwayListener>
          </Box>
          <Box
            sx={{
              height: '40%',
              width: '100%',
              color: '#000',
              padding: '0px 10px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: '1000',
                fontSize: matches ? '20px' : '30px',
                color: selectedMainKpi === 'daily' ? KPI_CARD_COLOR : DEFAULT_TEXT_COLOR,
              }}
            >
              {' '}
              {currency && exchangeRates[currency].symbol}
              {formatCurrency(
                String(
                  matches
                    ? abbreviateNumber(convertCurrency(currency, marketLevelDailyRate))
                    : convertCurrency(currency, marketLevelDailyRate)
                )
              )}
            </Typography>
          </Box>
          {selectedMainKpi === 'daily' && (
            <Box
              sx={{
                height: '28%',
                width: '100%',
                color: '#000',
                textAlign: 'end',
                position: 'relative',
                top: '-5px',
                right: '5px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  //   padding: '0px 20px 5px 0px',
                  color:
                    dailyYoYChange && Number(dailyYoYChange) > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                }}
              >
                {matches ? '' : 'YoY Change:'}{' '}
                {dailyYoYChange ? (
                  dailyYoYChange
                ) : (
                  <LockSharpIcon style={{ fontSize: 20, color: 'inherit' }} />
                )}
                {dailyYoYChange != 'N/A' ? '%' : ''}{' '}
              </Typography>
            </Box>
          )}
        </Box>

        {/* no of listings box */}
        <Box
          sx={{
            height: '100%',
            flex: 1,
            ...(selectedMainKpi === 'listing' && {
              boxShadow: '0 4px 10px #0001',
            }),
            ':hover': {
              cursor: 'pointer',
            },
          }}
          onClick={() => {
            setSelectedMainKpi('listing');
          }}
        >
          {selectedMainKpi === 'listing' && (
            <Box
              sx={{
                height: '4%',
                width: '100%',
                backgroundColor: KPI_CARD_COLOR,
              }}
            ></Box>
          )}
          <Box
            sx={{
              height: '28%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              color: '#000',
              padding: matches ? '3px' : '3px 10px',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: '500', fontSize: matches ? '12px' : '' }}>
              Active Listings
            </Typography>
            <ClickAwayListener onClickAway={() => setIsListingTooltipOpen(false)}>
              <InfoTooltip
                title={
                  <TooltipCard
                    title={'Active Listings'}
                    content={
                      'Listings available for guest bookings for 90 or more days in the past 12 months. The monthly graph shows those same listings that were active in each particular month.'
                    }
                    link=""
                  />
                }
                enterTouchDelay={0}
                leaveTouchDelay={6000}
                open={isListingTooltipOpen}
                onOpen={() => setIsListingTooltipOpen(true)}
                onClose={() => setIsListingTooltipOpen(false)}
              >
                <IconButton
                  style={{ padding: 3 }}
                  onClick={() => setIsListingTooltipOpen(prev => !prev)}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: matches ? '15px' : '17px', color: '#4aa59a' }}
                  />
                </IconButton>
              </InfoTooltip>
            </ClickAwayListener>
          </Box>
          <Box
            sx={{
              height: '40%',
              width: '100%',
              color: '#000',
              padding: '0px 10px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: '1000',
                fontSize: matches ? '20px' : '30px',
                color: selectedMainKpi === 'listing' ? KPI_CARD_COLOR : DEFAULT_TEXT_COLOR,
              }}
            >
              {' '}
              {formatCurrency(marketLevelActiveListings.toString())}
            </Typography>
          </Box>
          {selectedMainKpi === 'listing' && (
            <Box
              sx={{
                height: '28%',
                width: '100%',
                color: '#000',
                textAlign: 'end',
                position: 'relative',
                top: '-5px',
                right: '5px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  //   padding: '0px 20px 5px 0px',
                  color:
                    listingYoYChange && Number(listingYoYChange) > 0
                      ? COLOR_SUCCESS_1
                      : COLOR_ERROR_1,
                }}
              >
                {matches ? '' : 'YoY Change:'}{' '}
                {listingYoYChange ? (
                  listingYoYChange
                ) : (
                  <LockSharpIcon style={{ fontSize: 20, color: 'inherit' }} />
                )}
                {listingYoYChange != 'N/A' ? '%' : ''}{' '}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Graph Box */}
      <Box
        sx={{
          height: '60%',
          width: '100%',
          pointerEvents: isAccessDenied ? 'none' : 'auto',
        }}
      >
        {isGraphDataPresent && (
          <>
            <ResponsiveContainer
              width="100%"
              height="100%"
              style={{
                backgroundColor: 'red',
              }}
            >
              <LineChart
                data={
                  isAccessDenied
                    ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod]
                    : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod]
                }
                margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                  tickFormatter={combineFormatterYAxis}
                  domain={selectedMainKpi.includes('occupancy') && [0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Line
                  type="monotone"
                  dataKey={`LTMData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke={COLOR_PRIMARY_2}
                  strokeWidth={3}
                />
                {timePeriod == 24 && (
                  <Line
                    type="monotone"
                    dataKey={`last2YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                    stroke="#4FA2B1"
                    strokeDasharray="5 5"
                  />
                )}
                {timePeriod == 36 && (
                  <Line
                    type="monotone"
                    dataKey={`last2YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                    stroke={COLOR_PRIMARY_1_800}
                    strokeWidth={3}
                  />
                )}
                {timePeriod == 36 && (
                  <Line
                    type="monotone"
                    dataKey={`last3YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                    stroke={COLOR_PRIMARY_2_300}
                    strokeWidth={3}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
        {isAccessDenied && (
          <div
            style={{
              height: '76%',
              position: 'relative',
              top: '-103%',
              left: '84px',
              width: '90%',
              background: '#ffffff',
              filter: 'blur( 6px)',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          ></div>
        )}
        {isAccessDenied && (
          <Typography
            variant="body1"
            sx={{
              position: 'relative',
              top: '-150%',
              left: upgradeMessage() == EXPERT_CTA_FOR_TAILOR_REGION ? '30%' : '40%',
              fontSize: '20px',
              fontWeight: 500,
              color: COLOR_PRIMARY_2,
              cursor: 'pointer',
              pointerEvents: 'auto',
              textDecoration: 'underline',
            }}
            onClick={() => {
              handleOnSampleClick(timePeriod == 36 || !userTailorMarketAccess ? 'expert' : 'pro');
            }}
          >
            {upgradeMessage()}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          height: '10%',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* timePeriod dropdown */}
        <Box
          sx={{
            height: '100%',
            width: '200px',
          }}
        >
          <FormControl variant="standard" size="small" sx={{ width: 150, padding: '0px 20px' }}>
            <Select
              labelId="time-period-select"
              id="time-period-select"
              value={`${timePeriod}`}
              onChange={handleTimePeriodChange}
              label="Time Period"
            >
              <MenuItem value={24}>Last 2 years</MenuItem>
              <MenuItem value={36}>Last 3 years</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Percentile dropdown */}
        {selectedMainKpi !== 'listing' && (
          <Box
            sx={{
              height: '100%',
              width: '200px',
            }}
          >
            <FormControl variant="standard" size="small" sx={{ width: 150, padding: '0px 20px' }}>
              <Select
                labelId="percentile-select"
                id="percentile-select"
                value={`${selectedPercentile}`}
                onChange={event => {
                  isAccessDenied
                    ? handleOnSampleClick(!userTailorMarketAccess ? 'expert' : 'pro')
                    : setSelectedpercentile(event.target.value);
                }}
                label="Time Period"
              >
                <MenuItem value={90}>
                  Top 10%{' '}
                  {isAccessDenied && (
                    <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
                  )}
                </MenuItem>
                <MenuItem value={75}>
                  Top 25%{' '}
                  {isAccessDenied && (
                    <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
                  )}
                </MenuItem>
                <MenuItem value={50}>Median </MenuItem>
                <MenuItem value={25}>
                  Top 75%{' '}
                  {isAccessDenied && (
                    <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
                  )}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        {selectedMainKpi === 'listing' && (
          <Box
            sx={{
              height: '100%',
              width: '200px',
            }}
          >
            <FormControl variant="standard" size="small" sx={{ width: 150, padding: '0px 20px' }}>
              <Select
                labelId="percentile-select"
                id="percentile-select"
                value={'50'}
                onChange={event => {
                  setSelectedpercentile(event.target.value);
                }}
                label="Time Period"
              >
                <MenuItem value={50}>Median</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MainKPICard;
